import { z } from 'zod';

/**
 * A Mongo ObjectId as seen from outside the API.
 *
 * CLAUDE.md §6: this package must not import mongoose, so an ObjectId is a
 * regex-checked string here. It is cast to `Types.ObjectId` at the API boundary
 * (`api/src/utils/objectId.util.ts`) and nowhere else.
 */
export const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

export const objectIdSchema = z.string().regex(OBJECT_ID_REGEX, 'Must be a 24-character hexadecimal ObjectId');

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

export const SORT_ORDERS = ['asc', 'desc'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

/**
 * Every list query is paginated and bounded (CLAUDE.md §7) — `limit` is capped in the
 * schema so an unbounded `find()` cannot be requested from outside.
 */
export const basePaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(PAGINATION.DEFAULT_PAGE),
  limit: z.coerce.number().int().min(1).max(PAGINATION.MAX_LIMIT).default(PAGINATION.DEFAULT_LIMIT),
  sortOrder: z.enum(SORT_ORDERS).default('desc'),
});

export type BasePagination = z.infer<typeof basePaginationSchema>;

/**
 * Builds the pagination schema for one entity.
 *
 * `sortBy` is a `z.enum` allowlist and never an open `z.string()` (CLAUDE.md §6) —
 * an open sort field lets a caller sort by, and therefore probe, an unindexed or
 * unexposed column.
 *
 * @example
 *   const listItemsQuerySchema = createPaginationSchema(['createdAt', 'displayName'], 'createdAt');
 */
/**
 * ⚠️ [amended C3c.1] THE DIRECTION IS A PARAMETER NOW, BECAUSE A DEFAULT SORT FIELD AND A
 * DEFAULT DIRECTION ARE ONE DECISION AND WERE BEING TAKEN IN TWO PLACES.
 *
 * `basePaginationSchema` defaults `sortOrder` to `'desc'`, which is right for every list whose
 * natural default is "newest first". [C3c §8a] changed `listArrangementsQuerySchema`'s default
 * FIELD to `firstDutyDate` — "what is on next" — and left the direction alone, so a caller that
 * omitted `sortOrder` got the seven-day window ordered FURTHEST-OUT FIRST: the exact opposite of
 * the requirement, of the service's own comment, and of the test that covers it (which passes
 * `sortOrder=asc` explicitly and therefore could not see it).
 *
 * ⚠️ IT DEFAULTS TO `'desc'`, SO EVERY OTHER LIST IN THE REPOSITORY IS BYTE-IDENTICAL. Only a
 * schema that asks for `'asc'` gets it.
 */
export const createPaginationSchema = <const T extends readonly [string, ...string[]]>(
  sortableFields: T,
  defaultSortBy: T[number],
  defaultSortOrder: (typeof SORT_ORDERS)[number] = 'desc',
) =>
  basePaginationSchema.extend({
    sortBy: z.enum(sortableFields).default(defaultSortBy),
    sortOrder: z.enum(SORT_ORDERS).default(defaultSortOrder),
  });

/** The `:id` path parameter, shared by every by-id route. */
export const idParamSchema = z.object({
  id: objectIdSchema,
});

export type IdParam = z.infer<typeof idParamSchema>;
